import { useState, useRef, useEffect } from 'react';
import ProjectTreePanel from './ProjectTreePanel';
import DraggableComponent from '../DraggableComponent';
import WindowComponent from '../WindowComponent';
import WindowButton from '../buttons/WindowButton';
import { WindowContent, WindowHeader } from 'react95';
import styled from 'styled-components';
import { projects, projectGroups, portfolioPdfUrl } from './projects';
import ProfileOverview, { ExternalLink } from './ProfileOverview';
import PdfDownload from '../buttons/PdfDownload';
import './portfoliowindow.scss';

const Wrapper = styled.div`position: absolute;`;

const ProjectDetails = ({ project }) => (
  <article className="project-details">
    <span className="file-label">{project.period} / {project.status}</span>
    <h1>{project.title}</h1>
    <div className="technology-list" aria-label="Tecnologias">
      {project.technologies.map((technology) => <span key={technology}>{technology}</span>)}
    </div>
    {project.image && <figure className="project-image">
      <img src={project.image} alt={project.imageAlt} />
      <figcaption>{project.imageCaption}</figcaption>
    </figure>}
    <section><h2>Contexto e objetivo</h2><p>{project.summary}</p></section>
    <section><h2>Minha contribuição</h2><p>{project.contribution}</p></section>
    <section><h2>Resultado e estágio atual</h2><p>{project.outcome}</p></section>
    <div className="project-actions">
      <ExternalLink href={project.git} className="retro-link-button">Ver código no GitHub ↗</ExternalLink>
      {project.site && <ExternalLink href={project.site} className="retro-link-button">Abrir demonstração ↗</ExternalLink>}
      {(project.links || []).map((link) => <ExternalLink key={link.url} href={link.url}>{link.label} ↗</ExternalLink>)}
    </div>
  </article>
);

const PortfolioWindow = ({ displayingTask, displayTasks, setActiveTask, activeTask, indexingTasks, taskIndices, tasksVisibility, setTasksVisibility, setPortfolioHeight, setTouchStartY, setDocumentPosition, setTaskSwitiching }) => {
  const task = 'portfolio';
  const [isDraggable, setIsDraggable] = useState(true);
  const initialPosition = window.innerWidth <= 600 ? { x: 12, y: 12 } : { x: Math.max(20, (window.innerWidth - 880) / 2), y: 15 };
  const [selected, setSelected] = useState('projects');
  const [expanded, setExpanded] = useState(['projects', 'robotica']);
  const portfolioRef = useRef(null);
  const readingRef = useRef(null);
  const currentProject = projects[selected];
  const currentGroup = projectGroups.find((group) => group.id === selected);

  useEffect(() => {
    const element = portfolioRef.current;
    if (!element || !displayTasks.has(task)) {
      setPortfolioHeight(0);
      return;
    }
    const updateHeight = () => setPortfolioHeight(element.clientHeight);
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);
    return () => observer.disconnect();
  }, [displayTasks, setPortfolioHeight]);

  useEffect(() => {
    if (readingRef.current) readingRef.current.scrollTop = 0;
  }, [selected]);

  const selectProject = (id) => {
    setSelected(id);
    setExpanded((previous) => [...new Set([...previous, 'projects', projects[id].category])]);
  };
  const handleTouchStart = (event) => {
    setTouchStartY(event.touches[0].clientY);
    setDocumentPosition(document.documentElement.scrollTop);
  };

  return (
    <DraggableComponent task={task} initialPosition={initialPosition} setActiveTask={setActiveTask} indexingTasks={indexingTasks} isDraggable={isDraggable} setIsDraggable={setIsDraggable}>
      <Wrapper className="drag-portfolio" ref={portfolioRef} style={{ zIndex: taskIndices[task], display: displayTasks.has(task) ? 'block' : 'none', visibility: tasksVisibility.portfolio }}>
        <WindowComponent task={task} setActiveTask={setActiveTask} indexingTasks={indexingTasks} handleTouchStart={handleTouchStart}>
          <strong className="cursor"><WindowHeader active={activeTask === task} className="window-title">
            <span>Portfolio — João Pedro</span>
            <div className="buttons">
              <WindowButton purpose="minimise" tasksVisibility={tasksVisibility} task={task} setTasksVisibility={setTasksVisibility} setActiveTask={setActiveTask} setTaskSwitiching={setTaskSwitiching} setIsDraggable={setIsDraggable} />
              <WindowButton purpose="close" task={task} setActiveTask={setActiveTask} displayingTask={displayingTask} setIsDraggable={setIsDraggable} />
            </div>
          </WindowHeader></strong>
          <div className="portfolio-toolbar">
            <button onClick={() => setSelected('projects')} className="toolbar-home">⌂ Meu perfil</button>
            <span className="portfolio-location">{currentProject ? currentProject.title : currentGroup ? currentGroup.label : 'João Pedro / Portfólio'}</span>
            <PdfDownload href={portfolioPdfUrl} filename="Joao_Pedro_Santos_Portfolio.pdf" compact>PDF ↓</PdfDownload>
          </div>
          <WindowContent className="window-content">
            <ProjectTreePanel active={activeTask === task} displayTasks={displayTasks} selected={selected} setSelected={setSelected} expanded={expanded} setExpanded={setExpanded} />
            <div className="portfolio-reading-pane" ref={readingRef} tabIndex={0} aria-label="Conteúdo do portfólio">
              {currentProject ? <ProjectDetails project={currentProject} /> : currentGroup ? (
                <section className="project-category">
                  <span className="file-label">PASTA DE PROJETOS</span>
                  <h1>{currentGroup.label}</h1>
                  <p>Selecione um trabalho para explorar sua proposta, tecnologias e minha participação.</p>
                  <div className="featured-projects">
                    {Object.entries(projects).filter(([, project]) => project.category === selected).map(([id, project]) => (
                      <button key={id} className="featured-project" onClick={() => selectProject(id)}>
                        <span><span className="featured-title">{project.title}</span><span className="featured-meta">{project.technologies.join(' · ')}</span></span><span aria-hidden="true">→</span>
                      </button>
                    ))}
                  </div>
                </section>
              ) : <ProfileOverview onSelectProject={selectProject} />}
            </div>
          </WindowContent>
          <div className="portfolio-status"><span>{Object.keys(projects).length} projetos · 3 áreas</span><span>Sergipe, Brasil</span></div>
        </WindowComponent>
      </Wrapper>
    </DraggableComponent>
  );
};

export default PortfolioWindow;
